import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FaEnvelope, FaMobileAlt, FaLock, FaEye, FaEyeSlash, FaArrowRight } from 'react-icons/fa';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [tab, setTab] = useState('email'); // 'email' | 'mobile'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});

  /* ---- Email login ---- */
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errs.email = 'Valid email is required';
    if (!password || password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const result = await login({ email, password });
      if (result.success) {
        toast.success('लॉगिन यशस्वी! Welcome back!');
        navigate('/dashboard');
      } else {
        toast.error(result.message || 'Login failed. Check your credentials.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  /* ---- Send OTP ---- */
  const handleSendOtp = async () => {
    if (!mobile.trim() || !/^\d{10}$/.test(mobile)) {
      setErrors({ mobile: 'Valid 10-digit mobile number required' });
      return;
    }
    setSendingOtp(true);
    try {
      await axios.post('/api/auth/send-otp', { mobile });
      setOtpSent(true);
      toast.success('OTP sent to your mobile!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  /* ---- OTP login ---- */
  const handleOtpLogin = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!mobile.trim() || !/^\d{10}$/.test(mobile)) errs.mobile = 'Valid mobile required';
    if (!otp.trim() || otp.length < 4) errs.otp = 'Enter valid OTP';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await axios.post('/api/auth/verify-otp', { mobile, otp });
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      toast.success('लॉगिन यशस्वी! Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center relative overflow-hidden px-4 py-8">
      {/* Mandala pattern background */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '800px',
            height: '800px',
            borderRadius: '50%',
            background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 40px, rgba(139,0,0,0.3) 41px, transparent 42px)',
            animation: 'mandalaRotate 60s linear infinite',
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 30px, rgba(220,20,60,0.2) 31px, transparent 32px)',
            animation: 'mandalaRotate 45s linear infinite reverse',
          }}
        />
      </div>

      {/* Decorative corner elements */}
      <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-[#8B0000]/10 to-transparent rounded-br-full" />
      <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-[#8B0000]/10 to-transparent rounded-tl-full" />

      {/* Login card */}
      <div className="w-full max-w-md relative z-10">
        {/* Brand header */}
        <div className="text-center mb-8">
          <Link to="/">
            <div className="text-[#B8860B] text-xl mb-2 font-['Noto_Sans_Devanagari']">ॐ</div>
            <h1 className="text-4xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-1">
              विवाह आरंभ
            </h1>
            <p className="text-sm text-gray-500 font-['Playfair_Display']">Premium Hindu Vanjari Matrimonial</p>
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-[#FFD700]/20 overflow-hidden">
          {/* Top decorative bar */}
          <div className="h-1.5 bg-gradient-to-r from-[#8B0000] via-[#FFD700] to-[#DC143C]" />

          <div className="p-8">
            <h2 className="text-xl font-['Playfair_Display'] font-bold text-center text-[#8B0000] mb-6">
              लॉगिन करा
            </h2>

            {/* Tab toggle */}
            <div className="flex bg-[#FFF8F0] rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => { setTab('email'); setErrors({}); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                  tab === 'email'
                    ? 'bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white shadow-md'
                    : 'text-gray-500 hover:text-[#8B0000]'
                }`}
              >
                <FaEnvelope /> Email Login
              </button>
              <button
                type="button"
                onClick={() => { setTab('mobile'); setErrors({}); setOtpSent(false); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                  tab === 'mobile'
                    ? 'bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white shadow-md'
                    : 'text-gray-500 hover:text-[#8B0000]'
                }`}
              >
                <FaMobileAlt /> Mobile OTP
              </button>
            </div>

            {/* Email Tab */}
            {tab === 'email' && (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#8B0000] mb-1.5">ईमेल (Email)</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                      placeholder="email@example.com"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#8B0000] mb-1.5">पासवर्ड (Password)</label>
                  <div className="relative">
                    <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setErrors({}); }}
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                <div className="flex justify-end">
                  <a href="#" className="text-sm text-[#DC143C] hover:underline">Forgot Password?</a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Login <FaArrowRight /></>
                  )}
                </button>
              </form>
            )}

            {/* Mobile Tab */}
            {tab === 'mobile' && (
              <form onSubmit={handleOtpLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#8B0000] mb-1.5">मोबाईल नंबर (Mobile)</label>
                  <div className="flex">
                    <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">+91</span>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '').slice(0, 10)); setErrors({}); }}
                      placeholder="9876543210"
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all"
                    />
                  </div>
                  {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-[#8B0000] rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                  >
                    {sendingOtp ? (
                      <div className="w-5 h-5 border-2 border-[#8B0000] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-[#8B0000] mb-1.5">OTP</label>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setErrors({}); }}
                        placeholder="Enter OTP"
                        maxLength={6}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-center text-xl tracking-[0.5em] focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all"
                      />
                      {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp}</p>}
                    </div>

                    <div className="flex justify-between items-center">
                      <button type="button" onClick={handleSendOtp} disabled={sendingOtp} className="text-sm text-[#DC143C] hover:underline">
                        Resend OTP
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Verify & Login <FaArrowRight /></>
                      )}
                    </button>
                  </>
                )}
              </form>
            )}

            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#B8860B]/30 to-transparent" />
              <span className="px-3 text-xs text-[#B8860B]">✦</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#B8860B]/30 to-transparent" />
            </div>

            {/* Register link */}
            <p className="text-center text-sm text-gray-600">
              New here?{' '}
              <Link to="/register" className="text-[#DC143C] font-semibold hover:underline">
                नोंदणी करा (Register)
              </Link>
            </p>
          </div>
        </div>

        {/* Bottom attribution */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <span className="text-[#B8860B]">Lyftek Solutions</span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
